import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { ArrowLeft, Plus, Minus, ArrowRight } from "lucide-react";
import { track } from "../lib/analytics";
import { useAuth } from "../auth/AuthContext";

const FAQS = [
  {
    q: "Is Ascend Higher therapy or medical care?",
    a: "No. Ascend Higher is for personal growth, organization, reflection and habit-building. It is not therapy, medical care, financial advice, legal advice, or crisis support, and it doesn't replace licensed professionals. If you are in crisis, please call or text 988 (US) or contact a licensed professional.",
  },
  {
    q: "Do I need a credit card for the free trial?",
    a: "No. The 3-day trial is completely card-free. Sign in with Google, build your roadmap, and only enter payment details if you decide to keep going.",
  },
  {
    q: "What does the AI coach actually do?",
    a: "It helps you build and adjust a personal daily roadmap across five pillars — financial, health, spirituality, relationships and endgame — and remembers every conversation so you don't have to start over each time. It can edit your roadmap mid-chat (add or move milestones) when you ask.",
  },
  {
    q: "Will the AI just be a generic motivational chatbot?",
    a: "No. It's tuned to be specific and direct: short actionable next steps, emotionally aware follow-up questions, and zero filler. You can pick the tone — Calm, Hard & Straight, No-BS, or Best Friend.",
  },
  {
    q: "Is voice coaching included?",
    a: "Voice is included on Advanced. Trial users get to try the voice call experience during their 3 days. If you stay on Basic after trial, the AI is text-only and you can upgrade to Advanced any time.",
  },
  {
    q: "Can I cancel anytime?",
    a: "Yes. Cancel from the Pricing page or by emailing support — your subscription ends at the end of the current billing cycle. No questions asked.",
  },
  {
    q: "How do you handle my data and privacy?",
    a: "Your roadmap and chat memory are private to you. Public share links you generate yourself are sanitized (free-text journaling is stripped). You can delete your account and all data at any time from Account settings.",
  },
  {
    q: "What's the difference between Basic and Advanced?",
    a: "Basic ($8.88/mo) gives you the daily roadmap, habit tracking, daily check-ins and text-based AI coaching with 20 messages a day. Advanced ($28.88/mo) adds voice coaching, deeper memory, longer conversations, weekly insights, and 200 messages a day.",
  },
  {
    q: "Does the AI remember me across sessions?",
    a: "Yes. The coach has persistent memory of your roadmap, your goals across the five pillars, and the context of past conversations. You don't have to re-explain yourself every day.",
  },
  {
    q: "Is there an iOS / Android app?",
    a: "The web app works on any phone or desktop. Native iOS and Android apps are in active preparation — sign up now and you'll get the apps the moment they ship.",
  },
];

export default function FAQPage() {
  const { user } = useAuth();
  const [open, setOpen] = useState(0);

  useEffect(() => {
    document.title = "FAQ — Ascend Higher AI Life Coach";
    track("faq_page_view");
  }, []);

  return (
    <div className="min-h-screen bg-[#f7f3ea]">
      <header className="px-6 py-6 max-w-5xl mx-auto flex items-center justify-between">
        <Link
          to={user ? "/dashboard" : "/"}
          className="inline-flex items-center gap-2 font-body text-[14px] text-[#2b3442] hover:text-[#486a8f]"
        >
          <ArrowLeft size={16} /> {user ? "Dashboard" : "Back"}
        </Link>
        <div className="flex items-center gap-1">
          <span className="font-display text-[20px] font-semibold text-[#0f1724]">ascend</span>
          <span className="font-display text-[20px] font-semibold text-[#486a8f]">higher</span>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 pb-24">
        <div className="text-center mb-12">
          <p className="font-mono-eyebrow text-[#486a8f] mb-3">Frequently asked</p>
          <h1 className="font-display text-[44px] md:text-[60px] leading-[1.05] text-[#0f1724] tracking-tight">
            Honest answers,{" "}
            <span className="italic font-light text-[#486a8f]">no fluff.</span>
          </h1>
        </div>

        <div className="rounded-2xl bg-white border border-[#eadfc9] overflow-hidden">
          {FAQS.map((f, i) => {
            const isOpen = open === i;
            return (
              <div key={i} className={`${i > 0 ? "border-t border-[#eadfc9]" : ""}`}>
                <button
                  data-testid={`faq-toggle-${i}`}
                  onClick={() => {
                    setOpen(isOpen ? -1 : i);
                    if (!isOpen) track("faq_opened", { question_index: i });
                  }}
                  className="w-full text-left p-6 md:p-7 flex items-start justify-between gap-6 hover:bg-[#faf6ec] transition-colors"
                >
                  <span className="font-display text-[18px] md:text-[20px] text-[#0f1724] tracking-tight leading-tight">
                    {f.q}
                  </span>
                  <span className="w-8 h-8 rounded-full bg-[#f2ecdc] flex items-center justify-center shrink-0 text-[#486a8f]">
                    {isOpen ? <Minus size={14} /> : <Plus size={14} />}
                  </span>
                </button>
                {isOpen && (
                  <div className="px-6 md:px-7 pb-6 -mt-2">
                    <p className="font-body text-[15px] text-[#4b5563] leading-relaxed">{f.a}</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* CTA */}
        <div className="mt-12 text-center">
          <p className="font-body text-[14px] text-[#6b7280] mb-4">Still have a question?</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/support">
              <Button variant="outline" className="h-11 px-6 rounded-full border-[#d9cfb8] text-[#2b3442]">
                Contact support
              </Button>
            </Link>
            <Link to="/start">
              <Button
                data-testid="faq-start-trial-btn"
                onClick={() => track("faq_cta_click")}
                className="h-11 px-6 rounded-full bg-[#486a8f] hover:bg-[#3a5a7c] text-white"
              >
                Start free trial <ArrowRight size={14} className="ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}
